import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { getAuthSession } from './auth.js';

/**
 * F-016 US2 (FR-008) — report "where am I" to the Studio as the designer
 * navigates, so the Cowork companion can follow along. Fire-and-forget
 * presence telemetry: EVERY failure is swallowed (reporting must never
 * affect navigation), nothing is awaited by the UI, dedupe means one PUT
 * per location change. Never audited (I-CC-01).
 */
export type ReportedContext = {
  readonly view: 'trial' | 'test-case' | 'none';
  readonly trialId?: string;
  readonly testCaseId?: string;
};

/** Derive the presence shape from the router location (pure). */
export const deriveWorkspaceContext = (
  pathname: string,
  trialId: string | undefined,
): ReportedContext => {
  const caseMatch = /^\/uat\/cases\/([^/]+)/.exec(pathname);
  if (caseMatch !== null && trialId !== undefined && trialId.length > 0) {
    const ref = decodeURIComponent(caseMatch[1] ?? '');
    return { view: 'test-case', trialId, testCaseId: ref };
  }
  if (pathname.startsWith('/uat') && trialId !== undefined && trialId.length > 0) {
    return { view: 'trial', trialId };
  }
  return { view: 'none' };
};

export type ContextPoster = (context: ReportedContext) => void;

/**
 * The default poster: keepalive PUT with the session bearer; silent on every
 * failure path (offline, 4xx, expired session ⇒ skipped entirely).
 */
export const postWorkspaceContext: ContextPoster = (context) => {
  const session = getAuthSession();
  if (session === undefined) return;
  const base = import.meta.env['VITE_API_URL'];
  if (!base) return;
  void fetch(`${base}/workspace/context`, {
    method: 'PUT',
    keepalive: true,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${session.idToken}`,
    },
    body: JSON.stringify(context),
  }).catch(() => undefined);
};

/** Dedupe wrapper: post only when the derived context actually changed. */
export const createNavigationReporter = (post: ContextPoster) => {
  let last = '';
  return (pathname: string, trialId: string | undefined): void => {
    const context = deriveWorkspaceContext(pathname, trialId);
    const key = JSON.stringify(context);
    if (key === last) return;
    last = key;
    post(context);
  };
};

const sharedReporter = createNavigationReporter(postWorkspaceContext);

/** Mounted once in the root route — invisible, renders nothing. */
export const NavigationReporter = (): null => {
  const location = useRouterState({ select: (state) => state.location });
  const report = useRef(sharedReporter);
  useEffect(() => {
    const search = location.search as { trialId?: string };
    report.current(location.pathname, search.trialId);
  }, [location.pathname, location.searchStr]);
  return null;
};
